import { useState, useRef, useEffect } from "react";
import { ExternalLink, Copy, Trash2, Forward, X, User, Check } from "lucide-react";
import { useNavigate } from "react-router-dom";
import axiosInstance from "../../APIs/axiosInstance";

const EMOJIS = ["👍", "❤️", "😂", "😢", "😡", "🔥", "🥺", "🎉"];
const STORAGE_KEY = "msg_reactions";

const getStoredReactions = () => {
  try { return JSON.parse(localStorage.getItem(STORAGE_KEY) || "{}"); }
  catch { return {}; }
};

const MessageBubble = ({ message, onDeleted }) => {
  const navigate = useNavigate();
  const [menuMode, setMenuMode] = useState(null); // 'float' | 'sheet' | null
  const [menuPos, setMenuPos] = useState(null);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [showForward, setShowForward] = useState(false);
  const [forwardConvos, setForwardConvos] = useState([]);
  const [forwardSent, setForwardSent] = useState(null);
  const [copied, setCopied] = useState(false);
  const [reaction, setReaction] = useState(
    () => getStoredReactions()[message.id] || message.reaction || null
  );

  const longPressTimer = useRef(null);
  const menuRef = useRef(null);

  const time = message.created_at
    ? new Date(message.created_at).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })
    : "";

  // ── Menu open helpers ───────────────────────────────────────────────────────
  const openFloat = (x, y) => {
    setMenuPos({
      x: Math.min(x, window.innerWidth - 230),
      y: Math.min(y, window.innerHeight - 300),
    });
    setMenuMode("float");
  };

  const openSheet = () => setMenuMode("sheet");

  const closeMenu = () => {
    setMenuMode(null);
    setMenuPos(null);
  };

  // ── Desktop: right-click ───────────────────────────────────────────────────
  const handleContextMenu = (e) => {
    e.preventDefault();
    openFloat(e.clientX, e.clientY);
  };

  // ── Mobile: long-press ────────────────────────────────────────────────────
  const handleTouchStart = () => {
    longPressTimer.current = setTimeout(openSheet, 500);
  };
  const cancelLongPress = () => clearTimeout(longPressTimer.current);

  // Close float menu on outside click
  useEffect(() => {
    if (menuMode !== "float") return;
    const close = (e) => {
      if (menuRef.current && !menuRef.current.contains(e.target)) closeMenu();
    };
    document.addEventListener("mousedown", close);
    return () => document.removeEventListener("mousedown", close);
  }, [menuMode]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Actions ────────────────────────────────────────────────────────────────
  const handleReaction = (emoji) => {
    const next = reaction === emoji ? null : emoji;
    setReaction(next);
    // Persist so reaction survives navigation / remount
    const stored = getStoredReactions();
    if (next) stored[message.id] = next;
    else delete stored[message.id];
    localStorage.setItem(STORAGE_KEY, JSON.stringify(stored));
    closeMenu();
  };

  const handleCopy = () => {
    if (message.text) navigator.clipboard.writeText(message.text);
    setCopied(true);
    setTimeout(() => setCopied(false), 1500);
    closeMenu();
  };

  const handleDelete = async () => {
    try {
      await axiosInstance.delete(`/posts/messages/delete/${message.id}/`);
      onDeleted?.(message.id);
    } catch {}
    setShowDeleteConfirm(false);
    closeMenu();
  };

  const loadForwardContacts = async () => {
    try {
      const res = await axiosInstance.get("/posts/conversations/");
      setForwardConvos(res.data?.results || res.data || []);
    } catch {}
  };

  const handleForward = async (convoId) => {
    try {
      await axiosInstance.post(`/posts/messages/${convoId}/`, {
        text: message.text || "",
        shared_post_id: message.shared_post?.id || null,
      });
      setForwardSent(convoId);
      setTimeout(() => { setShowForward(false); setForwardSent(null); }, 1500);
    } catch {}
  };

  // ── Shared menu items (used in both float + sheet) ────────────────────────
  const MenuContent = () => (
    <>
      {/* Emoji row */}
      <div className="flex items-center justify-around px-3 py-3 border-b border-white/[0.07]">
        {EMOJIS.map((emoji) => (
          <button
            key={emoji}
            onClick={() => handleReaction(emoji)}
            className="text-xl hover:scale-125 active:scale-110 transition-transform duration-150 relative"
          >
            {emoji}
            {reaction === emoji && (
              <span className="absolute -top-1 -right-1 w-2.5 h-2.5 bg-blue-500 rounded-full border border-black" />
            )}
          </button>
        ))}
      </div>

      {/* Copy */}
      {message.text && (
        <button
          onClick={handleCopy}
          className="w-full flex items-center gap-3 px-4 py-3 hover:bg-white/[0.06] transition-colors text-sm text-white"
        >
          {copied ? <Check className="w-4 h-4 text-green-400" /> : <Copy className="w-4 h-4 text-white/50" />}
          {copied ? "Copied!" : "Copy text"}
        </button>
      )}

      {/* Forward */}
      <button
        onClick={() => {
          loadForwardContacts();
          setShowForward(true);
          closeMenu();
        }}
        className="w-full flex items-center gap-3 px-4 py-3 hover:bg-white/[0.06] transition-colors text-sm text-white"
      >
        <Forward className="w-4 h-4 text-white/50" />
        Forward
      </button>

      {/* Delete — own messages only */}
      {message.fromMe && (
        <button
          onClick={() => { setShowDeleteConfirm(true); closeMenu(); }}
          className="w-full flex items-center gap-3 px-4 py-3 hover:bg-red-500/10 transition-colors text-sm text-red-400 border-t border-white/[0.06]"
        >
          <Trash2 className="w-4 h-4" />
          Delete message
        </button>
      )}
    </>
  );

  // ──────────────────────────────────────────────────────────────────────────
  return (
    <>
      <div
        className={`flex gap-2.5 ${message.fromMe ? "justify-end" : "justify-start"}`}
        onTouchStart={handleTouchStart}
        onTouchEnd={cancelLongPress}
        onTouchMove={cancelLongPress}
        onContextMenu={handleContextMenu}
      >
        {/* Avatar — other user */}
        {!message.fromMe && (
          <div
            className="w-8 h-8 rounded-full overflow-hidden bg-white/10 flex items-center justify-center shrink-0 mt-1 ring-1 ring-white/5 hover:ring-white/20 transition cursor-pointer"
            onClick={() => message.sender_username && navigate(`/profile/${message.sender_username}`)}
          >
            {message.sender_avatar ? (
              <img src={message.sender_avatar} alt={message.sender_username} className="w-full h-full object-cover" />
            ) : (
              <User className="w-4 h-4 text-white/30" />
            )}
          </div>
        )}

        <div className={`max-w-[72%] flex flex-col ${message.fromMe ? "items-end" : "items-start"}`}>
          {!message.fromMe && message.sender_username && (
            <span className="text-white/30 text-[10px] mb-1 ml-1">@{message.sender_username}</span>
          )}

          {/* Shared post card */}
          {message.shared_post && (
            <div
              onClick={() => navigate(`/post/${message.shared_post.id}`)}
              className={`w-56 rounded-2xl overflow-hidden border mb-1.5 cursor-pointer transition-all duration-300 hover:scale-[1.02] ${message.fromMe ? "border-white/20" : "border-white/10"}`}
            >
              {message.shared_post.image && (
                <img src={message.shared_post.image} alt="shared post" className="w-full h-40 object-cover" />
              )}
              <div className={`px-3 py-2 flex items-center justify-between ${message.fromMe ? "bg-white/10" : "bg-white/5"}`}>
                <div className="min-w-0">
                  <p className="text-white text-xs font-semibold">@{message.shared_post.username}</p>
                  {message.shared_post.caption && (
                    <p className="text-white/50 text-xs truncate w-36">{message.shared_post.caption}</p>
                  )}
                </div>
                <ExternalLink className="w-3.5 h-3.5 text-white/30 shrink-0" />
              </div>
            </div>
          )}

          {/* Text bubble */}
          {message.text && (
            <div
              className={`px-4 py-2.5 text-sm leading-relaxed select-text transition-all duration-200 ${
                message.fromMe ? "rounded-2xl rounded-br-md shadow-md" : "rounded-2xl rounded-bl-md backdrop-blur-sm border border-white/5"
              }`}
              style={
                message.fromMe
                  ? { background: "oklch(77.7% 0.152 181.912)", color: "oklch(98.8% 0.003 106.5)" }
                  : { background: "rgba(255,255,255,0.08)", color: "oklch(98.8% 0.003 106.5)" }
              }
            >
              {message.text}
            </div>
          )}

          {/* Reaction badge */}
          {reaction && (
            <button
              onClick={() => handleReaction(reaction)}
              title="Click to remove"
              className={`text-base mt-1 hover:scale-110 active:scale-95 transition-transform select-none
                px-2 py-0.5 rounded-full border
                ${message.fromMe ? "self-end" : "self-start"}`}
              style={{ background: "rgba(255,255,255,0.08)", borderColor: "rgba(255,255,255,0.12)" }}
            >
              {reaction}
            </button>
          )}

          {/* Time + seen */}
          <div className="flex items-center gap-1.5 mt-1">
            <span className="text-white/20 text-[10px]">{time}</span>
            {message.fromMe && (
              <span className="text-white/20 text-[10px]">
                {message.id?.toString().startsWith("temp") ? "Sending…" : "✓"}
              </span>
            )}
          </div>
        </div>
      </div>

      {/* ── Float context menu (desktop right-click) ──────────────────────── */}
      {menuMode === "float" && menuPos && (
        <div className="fixed inset-0 z-[100] pointer-events-none">
          <div
            ref={menuRef}
            className="absolute pointer-events-auto bg-[#1e1e1e] rounded-2xl border border-white/10 shadow-2xl shadow-black/80 overflow-hidden"
            style={{ left: menuPos.x, top: menuPos.y, minWidth: 210 }}
          >
            <MenuContent />
          </div>
        </div>
      )}

      {/* ── Bottom sheet (mobile long-press) ─────────────────────────────── */}
      {menuMode === "sheet" && (
        <div
          className="fixed inset-0 z-[100] bg-black/60 backdrop-blur-sm flex items-end"
          onClick={closeMenu}
        >
          <div
            className="w-full rounded-t-3xl overflow-hidden border-t border-white/10 pb-safe"
            style={{ background: "#1e1e1e" }}
            onClick={(e) => e.stopPropagation()}
          >
            {/* Handle */}
            <div className="flex justify-center pt-3 pb-1">
              <div className="w-10 h-1 rounded-full bg-white/20" />
            </div>
            <MenuContent />
            <div className="h-4" />
          </div>
        </div>
      )}

      {/* ── Delete confirm ────────────────────────────────────────────────── */}
      {showDeleteConfirm && (
        <div className="fixed inset-0 bg-black/70 backdrop-blur-sm z-[110] flex items-center justify-center px-6">
          <div className="bg-[#1e1e1e] rounded-2xl border border-white/10 p-6 w-full max-w-xs shadow-2xl">
            <h3 className="text-white font-semibold mb-2">Delete message?</h3>
            <p className="text-white/40 text-sm mb-5">This message will be removed for everyone.</p>
            <div className="flex gap-3">
              <button
                onClick={() => setShowDeleteConfirm(false)}
                className="flex-1 py-2.5 rounded-xl bg-white/10 text-white text-sm font-medium hover:bg-white/20 transition"
              >
                Cancel
              </button>
              <button
                onClick={handleDelete}
                className="flex-1 py-2.5 rounded-xl bg-red-500 text-white text-sm font-medium hover:bg-red-600 transition"
              >
                Delete
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── Forward picker ────────────────────────────────────────────────── */}
      {showForward && (
        <div className="fixed inset-0 bg-black/70 backdrop-blur-sm z-[110] flex items-end md:items-center md:justify-center">
          <div className="bg-[#1e1e1e] rounded-t-3xl md:rounded-2xl w-full md:max-w-sm border-t md:border border-white/10 p-5 max-h-[60vh] flex flex-col shadow-2xl">
            <div className="flex items-center justify-between mb-4 shrink-0">
              <h3 className="text-white font-semibold">Forward to</h3>
              <button onClick={() => setShowForward(false)} className="p-1.5 rounded-full hover:bg-white/10 transition">
                <X className="w-4 h-4 text-white/50" />
              </button>
            </div>
            <div className="overflow-y-auto flex-1 space-y-1">
              {forwardConvos.length === 0 ? (
                <p className="text-white/20 text-sm text-center py-8">No conversations</p>
              ) : (
                forwardConvos.map((convo) => {
                  const uname = convo?.other_user?.username || "Unknown";
                  const ava = convo?.other_user?.profile?.avatar;
                  const sent = forwardSent === convo.id;
                  return (
                    <button
                      key={convo.id}
                      onClick={() => handleForward(convo.id)}
                      disabled={sent}
                      className="w-full flex items-center gap-3 px-3 py-2.5 rounded-xl hover:bg-white/5 transition text-left"
                    >
                      <div className="w-10 h-10 rounded-full overflow-hidden bg-white/10 flex items-center justify-center shrink-0">
                        {ava ? (
                          <img src={ava} alt={uname} className="w-full h-full object-cover" />
                        ) : (
                          <User className="w-4 h-4 text-white/30" />
                        )}
                      </div>
                      <span className="text-white text-sm flex-1">@{uname}</span>
                      {sent && <span className="text-green-400 text-xs flex items-center gap-1"><Check className="w-3 h-3" /> Sent</span>}
                    </button>
                  );
                })
              )}
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default MessageBubble;
